import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { useAuth } from './context/AuthContext';
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  Row,
  Col,
  Card,
  InputGroup
} from 'react-bootstrap';

/**
 * 預算表管理組件
 * 提供各社、各屆、各科目的預算輸入和管理功能
 */
function BudgetManagement() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');

  // 狀態管理
  const [budgets, setBudgets] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [terms, setTerms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // 篩選條件
  const [selectedSsid, setSelectedSsid] = useState(user?.e_ssid || '');
  const [selectedTermId, setSelectedTermId] = useState('');

  // Modal 狀態
  const [showModal, setShowModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // add, edit
  const [currentBudget, setCurrentBudget] = useState({
    budget_id: null,
    ssid: '',
    term_id: '',
    s_id: '',
    amount_budgeted: 0,
    budget_detail: ''
  });

  // 批量新增狀態
  const [batchBudgets, setBatchBudgets] = useState([]);

  // 載入社別列表
  const loadSocieties = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSocieties(response.data);
      }
    } catch (err) {
      console.error('載入社別列表失敗:', err);
    }
  };

  // 載入屆別列表
  const loadTerms = async () => {
    try {
      const params = {};
      if (selectedSsid) params.ssid = selectedSsid;

      const response = await axios.get('/finance/terms', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const termsData = response.data.data;
        setTerms(termsData);
        
        // 如果還沒有選擇屆別，自動選擇啟用的屆別
        if (!selectedTermId && termsData.length > 0) {
          // 優先選擇 is_active = 1 的屆別
          const activeTerm = termsData.find(term => term.is_active === 1 || term.is_active === '1');
          if (activeTerm) {
            console.log('自動選擇啟用屆別:', activeTerm.term_name);
            setSelectedTermId(activeTerm.term_id);
          } else {
            // 如果沒有啟用的屆別，選擇最新的（第一個）
            console.log('沒有啟用屆別，選擇第一個:', termsData[0].term_name);
            setSelectedTermId(termsData[0].term_id);
          }
        }
      }
    } catch (err) {
      console.error('載入屆別列表失敗:', err);
    }
  };

  // 載入科目列表
  const loadSubjects = async () => {
    try {
      const params = { active_only: 'true' };
      // 如果有選擇社別，只載入該社別的科目
      if (selectedSsid) {
        params.ssid = selectedSsid;
      }

      const response = await axios.get('/finance/subjects', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSubjects(response.data.data);
      }
    } catch (err) {
      console.error('載入科目列表失敗:', err);
    }
  };

  // 載入預算列表
  const loadBudgets = async () => {
    if (!selectedSsid || !selectedTermId) {
      setBudgets([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/finance/budgets', {
        params: { ssid: selectedSsid, term_id: selectedTermId },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setBudgets(response.data.data);
      }
    } catch (err) {
      setError('載入預算列表失敗：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // 初始化載入
  useEffect(() => {
    loadSocieties();
  }, []);

  // 當社別改變時載入屆別和科目
  useEffect(() => {
    if (selectedSsid) {
      // 清空當前選擇的屆別，讓 loadTerms 可以重新設定預設值
      setSelectedTermId('');
      loadTerms();
      loadSubjects();
    } else {
      setTerms([]);
      setSubjects([]);
      setSelectedTermId('');
    }
  }, [selectedSsid]);

  // 當篩選條件改變時載入預算
  useEffect(() => {
    loadBudgets();
  }, [selectedSsid, selectedTermId]);

  // 開啟新增 Modal
  const handleAdd = () => {
    setModalMode('add');
    setCurrentBudget({
      budget_id: null,
      ssid: selectedSsid,
      term_id: selectedTermId,
      s_id: '',
      amount_budgeted: 0,
      budget_detail: ''
    });
    setShowModal(true);
  };

  // 開啟編輯 Modal
  const handleEdit = (budget) => {
    setModalMode('edit');
    setCurrentBudget(budget);
    setShowModal(true);
  };

  // 儲存預算
  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!currentBudget.ssid || !currentBudget.term_id || !currentBudget.s_id) {
        setError('社別、屆別和科目為必填欄位');
        return;
      }

      if (currentBudget.amount_budgeted < 0) {
        setError('預算金額不可為負數');
        return;
      }

      if (modalMode === 'add') {
        const response = await axios.post('/finance/budgets', currentBudget, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess('預算新增成功！');
          setShowModal(false);
          loadBudgets();
        }
      } else {
        const response = await axios.put(`/finance/budgets/${currentBudget.budget_id}`, {
          amount_budgeted: currentBudget.amount_budgeted,
          budget_detail: currentBudget.budget_detail
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess('預算更新成功！');
          setShowModal(false);
          loadBudgets();
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || '儲存失敗');
    }
  };

  // 刪除預算
  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除此預算嗎？')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      const response = await axios.delete(`/finance/budgets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        loadBudgets();
      }
    } catch (err) {
      setError(err.response?.data?.error || '刪除失敗');
    }
  };

  // 開啟批量新增 Modal
  const handleBatchAdd = () => {
    if (!selectedSsid || !selectedTermId) {
      setError('請先選擇社別和屆別');
      return;
    }

    // 初始化批量預算（所有科目）
    const initialBudgets = subjects.map(subject => ({
      s_id: subject.s_id,
      s_code: subject.s_code,
      s_title: subject.s_title,
      s_type: subject.s_type,
      amount_budgeted: 0,
      budget_detail: ''
    }));

    setBatchBudgets(initialBudgets);
    setShowBatchModal(true);
  };

  // 更新批量預算金額
  const updateBatchAmount = (index, value) => {
    const updated = [...batchBudgets];
    updated[index].amount_budgeted = parseFloat(value) || 0;
    setBatchBudgets(updated);
  };

  // 更新批量預算明細
  const updateBatchDetail = (index, value) => {
    const updated = [...batchBudgets];
    updated[index].budget_detail = value;
    setBatchBudgets(updated);
  };

  // 批量儲存預算
  const handleBatchSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      // 過濾出有金額的預算
      const validBudgets = batchBudgets.filter(b => b.amount_budgeted > 0);

      if (validBudgets.length === 0) {
        setError('請至少輸入一個科目的預算金額');
        return;
      }

      const response = await axios.post('/finance/budgets/batch', {
        ssid: selectedSsid,
        term_id: selectedTermId,
        budgets: validBudgets
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess(`成功新增 ${response.data.count} 個科目的預算！`);
        setShowBatchModal(false);
        loadBudgets();
      }
    } catch (err) {
      setError(err.response?.data?.error || '批量新增失敗');
    }
  };

  // 格式化金額
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // 計算統計
  const stats = {
    income: budgets.filter(b => b.s_type === 'income').reduce((sum, b) => sum + parseFloat(b.amount_budgeted), 0),
    expense: budgets.filter(b => b.s_type === 'expense').reduce((sum, b) => sum + parseFloat(b.amount_budgeted), 0),
    count: budgets.length
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex align-items-center mb-4">
        <h2 className="mb-0">💰 財務管理 - 預算表管理</h2>
        <Button
          variant="link"
          onClick={() => setShowHelpModal(true)}
          className="ms-2 text-decoration-none"
          title="查看說明"
        >
          <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
        </Button>
      </div>

      {/* 錯誤/成功訊息 */}
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* 篩選工具列 */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>社別 <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={selectedSsid}
                  onChange={(e) => setSelectedSsid(e.target.value)}
                >
                  <option value="">請選擇社別</option>
                  {societies.map(society => (
                    <option key={society.ss_id} value={society.ss_id}>
                      {society.ss_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>屆別 <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  value={selectedTermId}
                  onChange={(e) => setSelectedTermId(e.target.value)}
                  disabled={!selectedSsid}
                >
                  <option value="">請選擇屆別</option>
                  {terms.map(term => (
                    <option key={term.term_id} value={term.term_id}>
                      {term.term_name}（第 {term.term_no} 屆）
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                variant="success"
                onClick={handleBatchAdd}
                disabled={!selectedSsid || !selectedTermId}
                className="me-2"
              >
                📋 批量新增預算
              </Button>
              <Button
                variant="primary"
                onClick={handleAdd}
                disabled={!selectedSsid || !selectedTermId}
              >
                ➕ 新增單筆預算
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 統計卡片 */}
      {selectedSsid && selectedTermId && budgets.length > 0 && (
        <Row className="mb-4">
          <Col md={4}>
            <Card bg="success" text="white">
              <Card.Body>
                <Card.Title>收入預算</Card.Title>
                <h3>{formatAmount(stats.income)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card bg="danger" text="white">
              <Card.Body>
                <Card.Title>支出預算</Card.Title>
                <h3>{formatAmount(stats.expense)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card bg="info" text="white">
              <Card.Body>
                <Card.Title>預算科目數</Card.Title>
                <h3>{stats.count} 個</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* 預算列表 */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : !selectedSsid || !selectedTermId ? (
        <Alert variant="info">
          請選擇社別和屆別以查看或建立預算
        </Alert>
      ) : budgets.length === 0 ? (
        <Alert variant="warning">
          此社別和屆別尚無預算資料，請點選「批量新增預算」或「新增單筆預算」開始建立預算表
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '100px' }}>科目代碼</th>
              <th>科目名稱</th>
              <th style={{ width: '80px' }}>類型</th>
              <th style={{ width: '150px' }}>預算金額</th>
              <th style={{ width: '120px' }}>建立時間</th>
              <th style={{ width: '150px' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {budgets.map(budget => (
              <tr key={budget.budget_id}>
                <td><code>{budget.s_code}</code></td>
                <td>{budget.s_title}</td>
                <td>
                  <Badge bg={budget.s_type === 'income' ? 'success' : 'danger'}>
                    {budget.s_type === 'income' ? '收入' : '支出'}
                  </Badge>
                </td>
                <td className="text-end">
                  <strong>{formatAmount(budget.amount_budgeted)}</strong>
                </td>
                <td className="text-muted small">
                  {new Date(budget.created_at).toLocaleDateString('zh-TW')}
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEdit(budget)}
                    className="me-2"
                  >
                    編輯
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(budget.budget_id)}
                  >
                    刪除
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* 新增/編輯單筆預算 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? '新增預算' : '編輯預算'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>科目 <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={currentBudget.s_id}
                onChange={(e) => setCurrentBudget({ ...currentBudget, s_id: e.target.value })}
                disabled={modalMode === 'edit'}
              >
                <option value="">請選擇科目</option>
                {subjects.map(subject => (
                  <option key={subject.s_id} value={subject.s_id}>
                    [{subject.s_type === 'income' ? '收入' : '支出'}] {subject.s_code} - {subject.s_title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>預算金額 <span className="text-danger">*</span></Form.Label>
              <InputGroup>
                <InputGroup.Text>NT$</InputGroup.Text>
                <Form.Control
                  type="number"
                  value={currentBudget.amount_budgeted}
                  onChange={(e) => setCurrentBudget({ ...currentBudget, amount_budgeted: e.target.value })}
                  min="0"
                  step="1000"
                />
              </InputGroup>
              <Form.Text className="text-muted">
                格式化顯示：{formatAmount(currentBudget.amount_budgeted)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>編列明細</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="請輸入預算編列明細說明（選填，最多約250字）"
                value={currentBudget.budget_detail || ''}
                onChange={(e) => setCurrentBudget({ ...currentBudget, budget_detail: e.target.value })}
                maxLength={250}
              />
              <Form.Text className="text-muted">
                已輸入 {(currentBudget.budget_detail || '').length} / 250 字
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {modalMode === 'add' ? '新增' : '更新'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 批量新增預算 Modal */}
      <Modal show={showBatchModal} onHide={() => setShowBatchModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>批量新增預算</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <Alert variant="info">
            請為各科目輸入預算金額，留空或輸入 0 的科目將不會建立預算。
          </Alert>

          <h5 className="text-success">收入類科目</h5>
          <Table striped bordered size="sm" className="mb-4">
            <thead className="table-success">
              <tr>
                <th style={{ width: '100px' }}>科目代碼</th>
                <th style={{ width: '150px' }}>科目名稱</th>
                <th style={{ width: '150px' }}>預算金額</th>
                <th>編列明細</th>
              </tr>
            </thead>
            <tbody>
              {batchBudgets.filter(b => b.s_type === 'income').map((budget, index) => {
                const actualIndex = batchBudgets.findIndex(b => b.s_id === budget.s_id);
                return (
                  <tr key={budget.s_id}>
                    <td><code>{budget.s_code}</code></td>
                    <td>{budget.s_title}</td>
                    <td>
                      <InputGroup size="sm">
                        <InputGroup.Text>NT$</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={budget.amount_budgeted}
                          onChange={(e) => updateBatchAmount(actualIndex, e.target.value)}
                          min="0"
                          step="1000"
                        />
                      </InputGroup>
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        size="sm"
                        rows={2}
                        placeholder="編列明細（選填）"
                        value={budget.budget_detail || ''}
                        onChange={(e) => updateBatchDetail(actualIndex, e.target.value)}
                        maxLength={250}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <h5 className="text-danger">支出類科目</h5>
          <Table striped bordered size="sm">
            <thead className="table-danger">
              <tr>
                <th style={{ width: '100px' }}>科目代碼</th>
                <th style={{ width: '150px' }}>科目名稱</th>
                <th style={{ width: '150px' }}>預算金額</th>
                <th>編列明細</th>
              </tr>
            </thead>
            <tbody>
              {batchBudgets.filter(b => b.s_type === 'expense').map((budget, index) => {
                const actualIndex = batchBudgets.findIndex(b => b.s_id === budget.s_id);
                return (
                  <tr key={budget.s_id}>
                    <td><code>{budget.s_code}</code></td>
                    <td>{budget.s_title}</td>
                    <td>
                      <InputGroup size="sm">
                        <InputGroup.Text>NT$</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={budget.amount_budgeted}
                          onChange={(e) => updateBatchAmount(actualIndex, e.target.value)}
                          min="0"
                          step="1000"
                        />
                      </InputGroup>
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        size="sm"
                        rows={2}
                        placeholder="編列明細（選填）"
                        value={budget.budget_detail || ''}
                        onChange={(e) => updateBatchDetail(actualIndex, e.target.value)}
                        maxLength={250}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <Alert variant="secondary" className="mt-3">
            <strong>預計建立：</strong>
            {batchBudgets.filter(b => b.amount_budgeted > 0).length} 個科目的預算
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBatchModal(false)}>
            取消
          </Button>
          <Button variant="success" onClick={handleBatchSave}>
            批量新增 ({batchBudgets.filter(b => b.amount_budgeted > 0).length} 個)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 說明 Modal */}
      <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>📌 預算表管理說明</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="mb-0">
            <li>每個社別的每個屆別都需要建立各科目的預算</li>
            <li>可以單筆新增或使用「批量新增」一次建立所有科目的預算</li>
            <li>建議使用批量新增功能，可以快速完成預算表的建立</li>
            <li>預算金額可隨時調整修改</li>
            <li>已有交易記錄的預算無法刪除</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHelpModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default BudgetManagement;
